/**
 * One scope of the circuit breaker's measurement, drawn.
 *
 * Lifted out of `BreakerCard` unchanged so the overview can show the sending
 * DOMAIN's rates with the same vocabulary the campaign panel uses. Two drawings
 * of the same measurement is how one screen ends up printing a comfortable
 * "0.00%" where the other prints "not enough letters yet" - and those are
 * opposite claims about the same seven days.
 *
 * WHY IT PRINTS "not enough letters yet" RATHER THAN 0.00%. Below the minimum
 * sample the server does not act on the rate. A clean-looking zero for a sender
 * that has posted nine letters reports a record where there is none, which is
 * the same mistake as a counter nothing writes.
 */
import { useT } from "../../../../i18n/I18nProvider";
import type { BreakerRates, BreakerView } from "../../../../lib/marketing";
import { rateText } from "../lib";
import { Stat, num } from "../shared";

/** One rate, or an honest admission that it cannot be measured yet. */
export function RateStat({
  label,
  rate,
  count,
  measurable,
  over,
}: {
  label: string;
  rate: number;
  count: number;
  measurable: boolean;
  over: boolean;
}) {
  const t = useT();
  return (
    <Stat
      label={label}
      value={measurable ? rateText(rate) : "-"}
      sub={measurable ? t("marketing.breaker.ofSent", { n: num(count) }) : t("marketing.breaker.tooFew")}
      tone={!measurable ? "muted" : over ? "bad" : "good"}
    />
  );
}

export function Scope({
  title,
  rates,
  windowDays,
  limits,
}: {
  title: string;
  rates: BreakerRates;
  windowDays: number;
  limits: BreakerView["limits"];
}) {
  const t = useT();
  return (
    <div className="space-y-2">
      <p className="crm-sub">
        <b>{title}</b> {t("marketing.breaker.window", { days: windowDays, n: num(rates.sent) })}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <RateStat
          label={t("marketing.breaker.bounces")}
          rate={rates.bounceRate}
          count={rates.bounced}
          measurable={rates.bounceMeasurable}
          over={rates.bounceRate > limits.bounce}
        />
        <RateStat
          label={t("marketing.breaker.complaints")}
          rate={rates.complaintRate}
          count={rates.complained}
          measurable={rates.complaintMeasurable}
          over={rates.complaintRate > limits.complaint}
        />
      </div>
    </div>
  );
}
